'use client';

import { useCallback } from 'react';

import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { useBrowserNotificationPermission } from '@/hooks/useBrowserNotifications';
import { useNotificationPreferences } from '@/hooks/queries/useNotificationPreferences';
import { notifyError } from '@/lib/notify-error';
import type { CourierPreferenceTopic } from '@/types/notifications';

/**
 * "Preferências de notificação" block on Perfil. Data and mutation live in
 * useNotificationPreferences (hook-per-resource rule); this component only renders.
 * Topic and section names come from the vendor workspace, so nothing here carries
 * per-environment ids. Renders nothing while the environment has notifications off.
 */
export function NotificationPreferencesSection() {
  const { enabled, topics, setTopic } = useNotificationPreferences();
  const { permission, requestPermission } = useBrowserNotificationPermission();

  const handleToggle = useCallback(
    async (topic: CourierPreferenceTopic, optedIn: boolean) => {
      try {
        await setTopic(topic.topic_id, optedIn);
      } catch (err) {
        notifyError('notification_preferences', err, 'Não foi possível salvar a preferência.');
      }
    },
    [setTopic],
  );

  const handleEnableBrowserNotifications = useCallback(async () => {
    try {
      await requestPermission();
    } catch (err) {
      notifyError(
        'browser_notification_permission',
        err,
        'Não foi possível solicitar a permissão do navegador.',
      );
    }
  }, [requestPermission]);

  const browserSupported = permission !== 'unsupported';
  if (!enabled || (topics.length === 0 && !browserSupported)) return null;

  const sections = new Map<string, CourierPreferenceTopic[]>();
  for (const topic of topics) {
    const list = sections.get(topic.section_name) ?? [];
    list.push(topic);
    sections.set(topic.section_name, list);
  }

  return (
    <div data-testid="notification-preferences" className="lg:pl-8">
      <div className="mb-4">
        <p className="font-mono text-2xs font-semibold tracking-[0.1em] text-text-tertiary uppercase">
          Preferências de notificação
        </p>
        <p className="mt-1 text-sm text-text-secondary">
          Escolha o que a Vértice pode te avisar e onde receber os alertas.
        </p>
      </div>
      <div className="space-y-5">
        {browserSupported && (
          <div>
            <p className="mb-2 text-sm font-semibold text-text-primary">No navegador</p>
            <div className="flex items-start justify-between gap-4">
              <p className="min-w-0 text-sm text-text-secondary">
                Receba avisos quando novas notificações chegarem enquanto a Vértice estiver aberta.
              </p>
              {permission === 'default' ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => void handleEnableBrowserNotifications()}
                  aria-label="Ativar notificações no navegador"
                  className="shrink-0"
                >
                  Ativar
                </Button>
              ) : (
                <p
                  className={`shrink-0 text-xs font-semibold ${
                    permission === 'granted' ? 'text-success' : 'text-text-tertiary'
                  }`}
                >
                  {permission === 'granted' ? 'Ativadas' : 'Bloqueadas'}
                </p>
              )}
            </div>
            {permission === 'denied' && (
              <p className="mt-2 text-xs text-text-tertiary">
                Reative a permissão nas configurações do navegador para receber estes avisos.
              </p>
            )}
          </div>
        )}
        {[...sections.entries()].map(([sectionName, sectionTopics]) => (
          <div key={sectionName}>
            <p className="mb-2 text-sm font-semibold text-text-primary">{sectionName}</p>
            <ul className="space-y-3" role="list">
              {sectionTopics.map((topic) => (
                <li key={topic.topic_id} className="flex items-center justify-between gap-4">
                  <span className="text-sm text-text-secondary">{topic.topic_name}</span>
                  <Switch
                    checked={topic.status !== 'OPTED_OUT'}
                    disabled={topic.status === 'REQUIRED'}
                    onCheckedChange={(checked) => void handleToggle(topic, checked)}
                    aria-label={`Notificações: ${topic.topic_name}`}
                  />
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}
